import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Box,
  Citrus,
  Coffee,
  Dumbbell,
  FlaskConical,
  RotateCcw,
  Zap,
} from "lucide-react";

export const drinksBeveragesPrompts: CategoryPrompt[] = [
  {
    icon: <FlaskConical />,
    title: "Create Subscription Personalization Quizzes",
    description:
      "Build a subscription quiz asking about preferences and consumption rate, then recommend subscription cadence and products. Make sure to use my product's selling plans.",
    prompt: `Build a subscription personalization quiz that matches customers to ideal beverage subscriptions based on taste preferences and consumption habits, recommending optimal delivery frequency.

**QUIZ FOCUS:** e.g., "Find Your Perfect Coffee Subscription" OR "Build Your Ideal Tea Delivery" OR "Your Custom Beverage Plan"

**SUBSCRIPTION OPTIONS:** e.g., Weekly, bi-weekly, monthly delivery with quantity options

**Quiz Taker Type:** Choose one:
- New subscribers (exploring subscription value)
- Heavy consumers (drink daily, need convenience)
- Variety seekers (want to try new flavors)

**Tone & Style:**
- Personalized beverage consultant
- Lifestyle and habit-focused
- Excitement about discovery
- Convenience and savings emphasis

**Quiz Question Guidelines:** Each question should:
- Use consumption behavior language
- Include visual product/lifestyle options
- Build toward cadence and variety needs
- Feel conversational not transactional
- 5-7 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (mid-commitment)
- Frame as "Get your personalized subscription plan"
- Offer incentive: "Unlock plan + 25% off first delivery"
- Single field: email only
- Trust line: "Join 40,000+ subscribers"

**Results Page Elements:**
- **Personalized Headline:** "Based on your habits, here's your perfect plan"
- **Recommended Cadence:** "Delivery every X weeks is ideal for you"
- **Quantity Suggestion:** How many bags/bottles per delivery
- **Curated Product Selection:** 3-6 beverages matched to taste preferences
- **Subscription Options:** Frequency selector with savings per option
- **Flavor Profile Match:** "You're a [bold/smooth/fruity] drinker"
- **Product Cards Include:** Image, price per delivery, flavor notes, subscription savings
- **Match Reasoning:** "You drink X cups daily, so this plan ensures you never run out"
- **Pricing Display:** Per delivery cost + cost per unit breakdown
- **Flexibility Messaging:** "Skip, pause, or swap flavors anytime"
- **Subscription Perks:** Savings %, free shipping, exclusive access
- **Alternative Cadences:** Other frequency options if their needs change
- **CTAs:** "Start Subscription" with selected plan details
- **Retake Option:** "Habits changed? Retake quiz"

**Conversion Tactics Use:**
- Cost-per-cup savings calculation
- Never-run-out convenience emphasis
- Taste profile personalization
- Discovery box options for variety seekers
- First delivery discount
- Easy modification messaging

**Key Quiz Questions to Include:**
- **Daily Consumption:** How many cups/servings per day
- **Household Size:** Solo drinker, couple, family
- **Taste Preference:** Bold, smooth, sweet, fruity, complex
- **Variety vs. Staple:** Same favorite or rotating flavors
- **Brewing Method:** Drip, espresso, cold brew, pour-over
- **Budget Comfort:** Value, mid-range, premium
- **Priority Feature:** Convenience, discovery, savings, quality

**Goal:** Convert hesitant subscribers into committed customers by recommending personalized subscription cadences and product selections that perfectly match their consumption rate and taste preferences while emphasizing savings and flexibility.`,
  },
  {
    icon: <Box />,
    title: "Create Problem-Solution Listicle Advertorials",
    description:
      "Build a listicle addressing common problems, recommending your functional beverage as the solution with CTAs throughout.",
    prompt: `Build a problem-solution listicle that identifies common daily struggles and positions your functional beverage as the natural, effective solution.

**TOPIC:** e.g., "7 Reasons Your Afternoon Crashes (And How to Fix Them)" OR "5 Morning Energy Mistakes Killing Your Productivity" OR "Why Your Coffee Habit Is Making You Worse"

**BEVERAGE SOLUTION:** e.g., "Our energy blend fixes problem #3" OR "Our adaptogenic coffee solves issue #5"

**Reader Type:** Choose one:
- Energy strugglers (need sustained focus)
- Health-conscious switchers (seeking better alternatives)
- Performance optimizers (want functional benefits)

**Tone & Style:**
- Empathetic to daily struggles
- Science-backed but accessible
- Solution-focused not preachy
- Relatable lifestyle language

**CTA Guidelines:** Each CTA should:
- Reference the problem solved ("No More Afternoon Crashes")
- Use functional benefit language ("Sustained Energy")
- Include ingredient proof ("Powered by Adaptogens")
- Emphasize natural solution ("Clean Energy")

**Conversion Tactics Use:**
- Problem agitation (make them feel the pain)
- Scientific explanations for why it happens
- Ingredient breakdowns showing solutions
- Before/after energy timeline comparisons
- Customer testimonials on specific fixes
- Functional benefit callouts

**Key Elements to Include:**
- **Hook:** Relatable daily struggle moment (2-3 paragraphs)
- **List Items (5-8 problems):** Numbered issue → why it happens → your beverage as solution
- **Problem Structure Per Item:**
  - Problem headline: "Problem #X: [Specific daily struggle]"
  - Why this happens: Scientific or lifestyle explanation
  - The cost: How it affects your day/health/performance
  - The solution: Your beverage with specific functional ingredient
  - How it works: Ingredient benefit explanation
  - Customer quote: Someone who solved this exact problem
- **Product Callouts:** Functional ingredients highlighted with "Try It" CTA
- **Comparison Boxes:** Regular coffee/energy drinks vs. your functional solution (2-3 throughout)
- **Ingredient Spotlight:** Key functional components explained (adaptogens, L-theanine, etc.)
- **Energy Timeline:** Before (crash pattern) vs. after (sustained energy)
- **Scientific Backing:** Study references or expert quotes on ingredients
- **CTAs:** After problem #2, after problem #5, conclusion (5-7 total)
- **FAQ:** 3-4 questions (Taste? Caffeine content? Works for everyone? How long to feel effects?)

**Goal:** Convert daily energy strugglers into loyal customers by identifying relatable problems and demonstrating how your functional beverage's specific ingredients provide scientifically-backed solutions that regular drinks can't offer.`,
  },
  {
    icon: <RotateCcw />,
    title: "Build a Use-Case Landing Page",
    description:
      "Create a page targeting [a specific use case] with ingredient science, third-party lab tests, and testimonials from relevant users.",
    prompt: `Build a use-case specific landing page that targets a functional need with scientific backing, lab verification, and social proof from relevant users.

**USE CASE:** e.g., "Pre-Workout Energy" OR "Focus for Studying" OR "Stress Relief" OR "Morning Alertness Without Jitters"

**FUNCTIONAL CLAIM:** e.g., "Sustained energy for 6+ hours" OR "Calm focus without crashes"

**Target User:** Choose one:
- Performance seekers (athletes, students, professionals)
- Health-conscious (want clean alternatives)
- Problem-specific (tired of jitters, crashes, anxiety)

**Tone & Style:**
- Results-focused and credible
- Science-backed not marketing fluff
- Specific to use case context
- Confidence through proof

**CTA Guidelines:** Each CTA should:
- Reference the use case ("Perfect Pre-Workout")
- Use outcome language ("Power Your Workout")
- Include proof element ("Lab-Tested")
- Emphasize suitability ("Made for Athletes")

**Conversion Tactics Use:**
- Third-party lab test results
- Ingredient dosage transparency
- Use-case specific testimonials
- Performance metrics from users
- Clinical study references
- Before/after performance comparisons

**Key Elements to Include:**
- **Hero Section:** Use case headline, result image, primary benefit, "Try It" CTA
- **The Problem:** Why this use case needs specific solution (2-3 paragraphs)
- **How It Works:** Ingredient breakdown with functional benefits per use case
- **Key Ingredients:** 3-5 active ingredients with dosages and science explanations
- **Third-Party Lab Tests:** Certificate of analysis, purity testing, potency verification
- **Clinical Backing:** Studies showing ingredient efficacy for this use case
- **Dosage Transparency:** Exact mg per serving with comparison to effective doses
- **Use-Case Testimonials (4-6):** Real users in same scenario with specific results
- **Performance Metrics:** "Improved focus by X%" or "Energy lasted Y hours"
- **Usage Instructions:** When/how to consume for optimal results in this scenario
- **Comparison Chart:** Your formula vs. competitors for this use case (3-5 rows)
- **Expert Validation:** Nutritionist, doctor, or specialist quote on ingredients
- **What's NOT In It:** Harmful ingredients excluded (relevant to use case)
- **Money-Back Guarantee:** Risk-free trial for this specific use
- **CTAs:** Hero, after science section, with testimonials, footer (5-6 total)
- **FAQ:** 5-7 questions (Safe for daily? Side effects? Works for everyone? Time to feel effects? Stack with other supplements?)

**Goal:** Convert use-case specific shoppers into confident buyers by providing overwhelming scientific proof, third-party verification, and relevant user testimonials that demonstrate your beverage delivers real, measurable results for their exact functional need.`,
  },
  {
    icon: <Coffee />,
    title: "Build a Flavor Quiz",
    description:
      "Build a flavor quiz with taste preference questions, showing top recommendations with explanations and variety pack options.",
    prompt: `Build a flavor quiz that matches customers to ideal beverages based on taste preferences with personalized recommendations and variety pack suggestions.

**QUIZ FOCUS:** e.g., "Find Your Perfect Flavor" OR "Discover Your Taste Match" OR "What's Your Flavor Profile?"

**FLAVOR RANGE:** e.g., Bold, smooth, fruity, nutty, spiced (8-15 SKUs to match from)

**Quiz Taker Type:** Choose one:
- New customers (exploring brand, unsure where to start)
- Flavor adventurers (want personalized discovery)
- Variety pack builders (need curated selection)

**Tone & Style:**
- Fun taste explorer energy
- Descriptive and sensory language
- Confident recommendations
- Discovery and excitement-focused

**Quiz Question Guidelines:** Each question should:
- Use taste and lifestyle descriptors
- Include visual flavor profiles or imagery
- Build toward specific flavor preferences
- Feel like personality discovery
- 5-7 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (mid-commitment)
- Frame as "Get your personalized flavor recommendations"
- Offer incentive: "Unlock matches + 20% off first order"
- Single field: email only
- Trust line: "Join 75,000+ flavor explorers"

**Results Page Elements:**
- **Personalized Headline:** "Your perfect match: [Flavor Profile Type]"
- **Flavor Profile Badge:** "You're a [Bold & Adventurous/Smooth & Classic/etc.] drinker"
- **Top 3 Recommendations:** Hero flavors with detailed taste descriptions
- **Flavor Match Reasoning:** "You said [answer], so this [flavor note] is perfect because..."
- **Tasting Notes:** Detailed flavor profiles (bright, citrus, smooth finish, etc.)
- **Variety Pack Suggestion:** Curated 3-6 pack based on their profile
- **Alternative Flavors:** 2-3 other options if they want variety
- **Product Cards Include:** Image, flavor description, intensity level, "Add to Cart"
- **Brewing/Serving Tips:** How to best enjoy their matched flavors
- **Customer Reviews:** Filtered to similar taste preferences
- **Bundle Savings:** "Try all 3 for 15% off"
- **CTAs:** "Try Your Match" and "Get Variety Pack"
- **Retake Option:** "Tastes changed? Retake quiz"

**Conversion Tactics Use:**
- Taste profile personalization ("You're a smooth drinker")
- Variety pack bundling (discovery at discount)
- Flavor intensity matching
- Pairing suggestions (food, time of day)
- Customer taste twin testimonials
- Limited sampler options

**Key Quiz Questions to Include:**
- **Flavor Intensity:** Mild, medium, bold, extra bold
- **Taste Preference:** Fruity, nutty, chocolatey, earthy, floral
- **Sweetness Level:** Unsweetened, lightly sweet, sweet
- **Adventure Level:** Stick to favorites or try new flavors
- **Morning vs Evening:** Bright/energizing or smooth/calming
- **Coffee/Tea Background:** What they currently drink
- **Flavor Dislikes:** Bitter, acidic, too sweet (elimination)

**Goal:** Convert overwhelmed flavor shoppers into confident buyers by providing personalized taste-matched recommendations that eliminate decision paralysis, with curated variety packs that encourage higher-value purchases and product discovery.`,
  },
  {
    icon: <Zap />,
    title: "Create Pairing Guides with Shoppable Products",
    description:
      "Build a pairing guide page with pairing recommendations, recipe cards, and shoppable product CTAs throughout.",
    prompt: `Build a pairing guide page that inspires usage through food/occasion pairings with shoppable products and recipe integrations.

**PAIRING FOCUS:** e.g., "Coffee & Breakfast Pairings" OR "Tea & Dessert Matches" OR "Energy Drinks & Workout Snacks" OR "Seasonal Beverage Pairings"

**PAIRING CATEGORIES:** e.g., By meal, by occasion, by season, by flavor profile

**Visitor Type:** Choose one:
- Experience enhancers (elevating daily rituals)
- Recipe seekers (want usage inspiration)
- Gift shoppers (creating bundles for others)

**Tone & Style:**
- Culinary-inspired and elevated
- Sensory and descriptive language
- Expert sommelier-style guidance
- Accessible not pretentious

**CTA Guidelines:** Each CTA should:
- Reference the pairing ("Shop This Combo")
- Use experience language ("Elevate Your Morning")
- Include bundle option ("Get the Pair")
- Emphasize complementary benefits

**Conversion Tactics Use:**
- Complete pairing bundles (beverage + suggested items)
- Recipe cards with integrated products
- Occasion-based recommendations
- Flavor science explanations (why they pair)
- Seasonal limited pairings
- Gift set suggestions from pairings

**Key Elements to Include:**
- **Hero Section:** Pairing guide headline, featured pairing image, "Shop Pairings" CTA
- **Introduction:** Philosophy on pairing beverages (2-3 paragraphs)
- **Pairing Categories:** Organized sections (breakfast, lunch, dessert, occasions)
- **Featured Pairings (8-12):**
  - Large pairing image
  - Beverage + food/occasion recommendation
  - Why they work together (flavor notes, texture, experience)
  - Shoppable product CTAs for beverage
  - Suggested food items or recipes
- **Recipe Cards (3-5):** Step-by-step with beverage integration and "Shop Ingredients"
- **Flavor Science:** Why certain flavors complement (sweet + bitter, etc.)
- **Occasion Guides:** Morning ritual, afternoon break, evening wind-down pairings
- **Seasonal Pairings:** Limited-time combinations for current season
- **Bundle Options:** Pre-made pairing sets with discount
- **Expert Tips:** Brewing methods for optimal pairing experience
- **Customer Pairing Photos:** UGC showing creative combinations
- **Gift Set Section:** Pairing bundles perfect for gifting
- **CTAs:** With each pairing, bundle sections, recipe cards (8-10 total)
- **FAQ:** 3-4 questions (Substitutions? Other pairing ideas? Gift packaging? Shelf life?)

**Goal:** Increase purchase frequency and basket size by inspiring elevated usage through curated pairings that make beverages feel like essential components of memorable experiences, with easy bundle purchasing that converts inspiration into immediate sales.`,
  },
  {
    icon: <Dumbbell />,
    title: "Launch Collaboration Drop Pages",
    description:
      "Create a collaboration page featuring partner's work and story, with exact drop time and SMS alert signup.",
    prompt: `Build a collaboration drop page that builds hype around a limited partnership through partner storytelling, exact launch timing, and alert signups.

**COLLABORATION PARTNER:** e.g., "x Local Roaster" OR "x Celebrity Chef" OR "x Wellness Brand"

**DROP TYPE:** e.g., "Limited-edition flavor" OR "Co-branded collection" OR "Exclusive blend"

**Audience Type:** Choose one:
- Brand loyalists (excited for new releases)
- Partner's followers (crossover audience)
- Limited-edition collectors (FOMO-driven)

**Tone & Style:**
- Exclusive and insider-access feel
- Story-rich and collaborative
- Hype-building but authentic
- Community celebration energy

**CTA Guidelines:** Each CTA should:
- Emphasize exclusivity ("Limited Drop")
- Reference collaboration ("Get the Collab")
- Use scarcity language ("Only X Units")
- Include alert signup ("Don't Miss It")

**Conversion Tactics Use:**
- Exact drop date/time countdown
- SMS/email alert signup for launch
- Limited quantity messaging
- Behind-the-scenes collaboration story
- Partner's credibility and following
- Pre-launch waitlist or early access

**Key Elements to Include:**
- **Hero Section:** Collaboration visual, partner logos, drop date/time, "Get Notified" CTA
- **Drop Countdown:** Large timer to exact launch time (days:hours:minutes:seconds)
- **SMS Alert Signup:** Phone/email capture for launch notification
- **The Collaboration Story:** How partnership came together, why it's special (3-4 paragraphs)
- **Partner Introduction:** Who they are, their credentials, why you partnered
- **Behind-the-Scenes:** Process photos, development journey, testing
- **Product Details:** What's being dropped (flavor profile, design, special features)
- **Limited Availability:** Exact quantity or "available while supplies last"
- **What Makes It Special:** Unique aspects only in this collaboration
- **Partner Quote/Video:** Their perspective on the collaboration
- **Early Access Option:** VIP or subscriber first access if applicable
- **Launch Day Plan:** What happens at drop time (link goes live, etc.)
- **Bundle Option:** If multiple items, complete collection offer
- **Drop Terms:** Purchase limits per customer, no restock messaging
- **Social Proof:** Hype from partner's community or previous drops
- **CTAs:** Hero alert signup, mid-page reminders, footer countdown (4-5 total)
- **FAQ:** 4-5 questions (Exact drop time? How to buy? Quantity? Restock? Shipping?)

**Goal:** Maximize launch-day sales through strategic hype-building and alert capture that ensures maximum audience presence at drop time, converting both brand loyalists and partner's followers through exclusive collaboration storytelling and scarcity urgency.`,
  },
  {
    icon: <Citrus />,
    title: "Build Competitor Comparison Pages",
    description:
      "Create a side-by-side comparison of ingredients, benefits, price, and taste with customer testimonials from switchers and money-back guarantee.",
    prompt: `Build a competitor comparison page that positions your beverage as superior through transparent side-by-side analysis and switcher testimonials.

**COMPARISON FOCUS:** e.g., "Us vs. [Competitor]" OR "Why Customers Switch from [Brand]" OR "The Better Alternative to [Product]"

**COMPARISON ANGLE:** e.g., Ingredients, price-per-serving, functional benefits, taste quality

**Visitor Type:** Choose one:
- Active comparers (researching between brands)
- Competitor customers (considering switch)
- Deal seekers (finding best value)

**Tone & Style:**
- Honest and factual not aggressive
- Confident in differences
- Respectful but clear about superiority
- Data-driven and transparent

**CTA Guidelines:** Each CTA should:
- Reference switching ("Make the Switch")
- Use comparison proof ("See the Difference")
- Include risk-free language ("Try Risk-Free")
- Emphasize value ("Better Quality, Better Price")

**Conversion Tactics Use:**
- Detailed side-by-side comparison charts
- Price-per-serving calculations
- Ingredient quality differences highlighted
- Switcher testimonials with reasons why
- Money-back guarantee for risk-free trial
- Taste test challenge offers

**Key Elements to Include:**
- **Hero Section:** Bold comparison headline, "Try the Better Option" CTA
- **Why People Switch:** Top 3-5 reasons customers leave competitors for you
- **Side-by-Side Comparison Chart:** Detailed rows (6-10 categories):
  - Ingredients/Formula
  - Quality/Sourcing
  - Functional benefits
  - Taste profile
  - Price per serving
  - Certifications/Testing
  - Sustainability
  - Customer rating
- **Ingredient Breakdown:** What's better about your formula with specifics
- **Price Analysis:** Cost-per-serving or monthly cost comparison with math shown
- **Taste Comparison:** Flavor profiles described, blind taste test results if available
- **Switcher Testimonials (5-8):** Former competitor customers explaining why they switched
- **Before/After Switch:** Customer experiences improving after switching
- **What You Get More Of:** Extra benefits they don't realize they're missing
- **What You Avoid:** Inferior ingredients or practices in competitor products
- **Money-Back Guarantee:** Risk-free trial offer for skeptical switchers
- **Sample/Trial Offer:** Low-risk first purchase option
- **Honest Acknowledgment:** If competitor has any advantages, address transparently
- **CTAs:** Hero, after comparison chart, with testimonials, guarantee section (5-6 total)
- **FAQ:** 4-5 questions (Taste really better? Worth switching? Guarantee terms? Subscription transfer?)

**Goal:** Convert competitor customers into switchers by providing transparent, factual comparison that highlights your superior value proposition, backed by real switcher testimonials and risk-free guarantee that removes purchase hesitation.`,
  },
];
